// src/store/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  auth,
  db,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from "./../api/firebase";
import { doc, getDoc } from "firebase/firestore";
import { addNotification } from "./notificationSlice"; // Importar para mostrar errores

export const fetchUserExtras = createAsyncThunk(
  "auth/fetchUserExtras",
  async (user, { dispatch, rejectWithValue }) => {
    if (!user.uid) return rejectWithValue("No user UID provided");

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();

        // **VERIFICACIÓN DE ESTADO ACTIVO**
        // Si el usuario está inactivo, cerramos su sesión inmediatamente.
        if (userData.isActive === false) {
          dispatch(signOut());
          dispatch(
            addNotification({
              type: "error",
              message:
                "Tu cuenta ha sido desactivada. Contacta al administrador.",
              isPersistent: true,
            })
          );
          return rejectWithValue("Usuario inactivo.");
        }

        const { roleId, tenantId } = userData;

        const roleDocRef = doc(db, `tenants/${tenantId}/roles`, roleId);
        const roleDocSnap = await getDoc(roleDocRef);

        if (roleDocSnap.exists()) {
          return { ...userData, permissions: roleDocSnap.data().permissions };
        } else {
          return rejectWithValue(`El rol '${roleId}' no existe.`);
        }
      } else {
        return rejectWithValue("El perfil de usuario no existe en Firestore.");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- MODIFICADO: Se añade 'return' para devolver la función unsubscribe ---
export const listenToAuthChanges = () => (dispatch) => {
  return onAuthStateChanged(auth, (user) => {
    // --- FIN MODIFICACIÓN ---
    if (user) {
      // Primero seteamos el usuario básico para que la UI se actualice rápido
      dispatch(setUser({ uid: user.uid, email: user.email }));
      // Luego buscamos los detalles y permisos.
      dispatch(fetchUserExtras(user));
    } else {
      dispatch(setUser(null));
      dispatch(setLoading(false));
    }
    // El loading principal se setea a false solo cuando no hay usuario
    // o cuando fetchUserExtras termina.
  });
};

export const signOut = createAsyncThunk("auth/signOut", async () => {
  await firebaseSignOut(auth);
  return null;
});

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Inicia en true hasta que el primer chequeo de auth termine.
  error: null,
  tenantId: null, // Añadido para acceso rápido
  roleId: null, // Añadido para acceso rápido
  permissions: {}, // Añadido para acceso rápido
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      if (!action.payload) {
        state.permissions = {};
        state.tenantId = null;
        state.roleId = null;
      }
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserExtras.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserExtras.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        // Guardamos los datos clave en el nivel superior para fácil acceso
        state.permissions = action.payload.permissions;
        state.tenantId = action.payload.tenantId;
        state.roleId = action.payload.roleId;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchUserExtras.rejected, (state, action) => {
        state.error = action.payload;
        state.isLoading = false;
        // Si falla la carga de extras, deslogueamos para evitar un estado inconsistente
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = {};
        state.tenantId = null;
        state.roleId = null;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = {};
        state.tenantId = null;
        state.roleId = null;
        state.error = null;
      });
  },
});

export const { setUser, setLoading, setError } = authSlice.actions;

export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.isLoading;

export default authSlice.reducer;
